<template>
  <section class="financial-account-summary-grid">
    <section class="financial-summary-dashboard-grid">
      <article class="customer-detail-card customer-detail-card--accent-primary financial-summary-kpi-card">
        <header class="financial-summary-kpi-card__header">
          <span>{{ text.summary.dashboard.balanceTitle }}</span>
          <strong>{{ kpis.currentBalanceLabel }}</strong>
        </header>

        <div class="financial-summary-kpi-card__meta-grid">
          <div>
            <span>{{ text.summary.dashboard.entriesMonth }}</span>
            <strong>{{ kpis.entriesMonthLabel }}</strong>
          </div>
          <div>
            <span>{{ text.summary.dashboard.exitsMonth }}</span>
            <strong>{{ kpis.exitsMonthLabel }}</strong>
          </div>
          <div>
            <span>{{ text.summary.dashboard.lastMovement }}</span>
            <strong>{{ kpis.lastMovementLabel }}</strong>
          </div>
        </div>
      </article>

      <article class="customer-detail-card customer-detail-card--accent-success financial-summary-chart-card">
        <header class="financial-summary-chart-card__header">
          <h4>{{ text.summary.dashboard.evolutionTitle }}</h4>

          <div class="financial-summary-chart-card__ranges" role="tablist" aria-label="Rangos de tiempo">
            <button
              v-for="range in rangeOptions"
              :key="range.key"
              type="button"
              class="financial-summary-chart-card__range-btn"
              :class="{ active: selectedRange === range.key }"
              @click="selectedRange = range.key"
            >
              {{ range.label }}
            </button>
          </div>
        </header>

        <div class="financial-summary-chart-card__body">
          <apexchart
            :key="chartRenderKey"
            type="area"
            height="230"
            :options="chartOptions"
            :series="chartSeries"
          />
        </div>
      </article>
    </section>

    <article class="customer-detail-card customer-detail-card--accent-warning financial-summary-recent-card">
      <header class="financial-summary-recent-card__header">
        <h4>{{ text.summary.dashboard.recentTitle }}</h4>
        <button type="button" class="financial-summary-recent-card__link financial-account-card__movements-btn" @click="$emit('open-movements')">
          {{ text.summary.dashboard.viewMovements }}
          <i class="fa fa-angle-right" aria-hidden="true"></i>
        </button>
      </header>

      <div class="financial-summary-recent-card__body">
        <div class="app-table-wrap">
          <div class="app-table-scroll">
            <table class="app-data-table">
              <thead>
                <tr>
                  <th>{{ text.movements.table.date }}</th>
                  <th>{{ text.movements.table.movementType }}</th>
                  <th>{{ text.movements.table.concept }}</th>
                  <th class="app-text-center financial-col-center">{{ recentTableHeaders.amount }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="recentMovements.length === 0">
                  <td colspan="4" class="app-table-empty">{{ text.summary.dashboard.emptyRecent }}</td>
                </tr>

                <tr v-for="movement in recentMovements" :key="movement.id">
                  <td>
                    <div class="app-strong">{{ movement.date }}</div>
                    <small class="app-muted">{{ movement.time }}</small>
                  </td>
                  <td>
                    <span
                      v-if="movement.category"
                      class="financial-category-pill"
                      :class="resolveCategoryClass(movement.categoryKey || movement.typeReference)"
                    >
                      {{ movement.category }}
                    </span>
                  </td>
                  <td>
                    <div class="app-strong">{{ movement.concept }}</div>
                  </td>
                  <td class="app-text-right">
                    <strong :class="movement.isIncome ? 'financial-amount-positive' : 'financial-amount-negative'">
                      {{ movement.amountLabel }}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </article>

    <section class="financial-summary-grid">
      <article class="customer-detail-card customer-detail-card--accent-primary">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-info-circle customer-section-icon customer-section-icon--info" aria-hidden="true"></i>
            {{ text.summary.general.title }}
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--info"
            :title="text.summary.general.editAction"
            @click="$emit('edit-section', 'general')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-grid">
          <div
            v-for="item in generalItems"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <span class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>

      <article v-if="showBankCard" class="customer-detail-card customer-detail-card--accent-success">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>
            <i class="fa fa-university customer-section-icon customer-section-icon--billing" aria-hidden="true"></i>
            {{ text.summary.bank.title }}
          </h4>

          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit customer-section-edit-btn customer-section-edit-btn--billing-edit"
            :title="text.summary.bank.editAction"
            @click="$emit('edit-section', 'bank')"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>
        </header>

        <div class="customer-detail-info-grid">
          <div
            v-for="item in bankItems"
            :key="item.label"
            class="customer-detail-info-item"
          >
            <span>{{ item.label }}</span>
            <span class="customer-detail-info-value">{{ item.value }}</span>
          </div>
        </div>
      </article>
    </section>
  </section>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';
import { normalizeYesNoLabel } from '@/utils/dataNormalization';
import { formatDateLong, formatDateShort } from '@/utils/datePresentation';
import { formatCurrencyDisplay } from '@/utils/numberFormat';
import financialAccountsService from '@/services/financialAccounts';
import { extractPayload } from '@/utils/dataNormalization';

const { catalog: domainText } = useDomainText('financial');

const DAY_MS = 24 * 60 * 60 * 1000;

const toTimestamp = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatAxisCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) {
    return '$0';
  }

  const absolute = Math.abs(amount);
  const formatted = new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(absolute);

  return `${amount < 0 ? '-' : ''}$${formatted}`;
};

export default {
  name: 'FinancialAccountDetailSummaryTab',
  emits: ['edit-section', 'open-movements'],
  props: {
    account: {
      type: Object,
      default: null,
    },
    financialAccountId: {
      type: [Number, String],
      default: 0,
    },
    statusLabel: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      text: domainText.detailAccount,
      loadingSummary: false,
      selectedRange: '30d',
      summaryData: {
        kpis: {
          current_balance: 0,
          entries_month: 0,
          exits_month: 0,
          last_movement_at: '',
          currency: 'MXN',
        },
        recent_movements: [],
        balance_evolution: [],
      },
    };
  },
  computed: {
    resolvedFinancialAccountId() {
      return Number(this.financialAccountId || 0);
    },
    fallbackValue() {
      return '';
    },
    accountCurrency() {
      return String(this.account?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    accountTypeCode() {
      return String(this.account?.type?.code || '').trim().toUpperCase();
    },
    showBankCard() {
      return ['BANK', 'CREDIT_CARD', 'BANK_CREDIT'].includes(this.accountTypeCode);
    },
    currencyCode() {
      return String(this.summaryData?.kpis?.currency || this.accountCurrency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    kpis() {
      const source = this.summaryData?.kpis || {};
      const currentBalance = Number(source.current_balance || this.account?.balance || 0);
      const entriesMonth = Number(source.entries_month || 0);
      const exitsMonth = Number(source.exits_month || 0);

      return {
        currentBalance,
        entriesMonth,
        exitsMonth,
        currentBalanceLabel: formatCurrencyDisplay(currentBalance, { currency: this.currencyCode }),
        entriesMonthLabel: formatCurrencyDisplay(entriesMonth, { currency: this.currencyCode }),
        exitsMonthLabel: formatCurrencyDisplay(exitsMonth, { currency: this.currencyCode }),
        lastMovementLabel: formatDateLong(source.last_movement_at, { fallback: '' }),
      };
    },
    rangeOptions() {
      return [
        { key: '30d', label: this.text.summary.dashboard.ranges.d30, days: 30 },
        { key: '3m', label: this.text.summary.dashboard.ranges.m3, days: 90 },
        { key: '6m', label: this.text.summary.dashboard.ranges.m6, days: 180 },
        { key: '1y', label: this.text.summary.dashboard.ranges.y1, days: 365 },
      ];
    },
    filteredBalanceEvolution() {
      const points = Array.isArray(this.summaryData?.balance_evolution)
        ? this.summaryData.balance_evolution
        : [];
      if (points.length === 0) {
        return [];
      }

      const selected = this.rangeOptions.find((range) => range.key === this.selectedRange) || this.rangeOptions[0];
      const endTimestamp = toTimestamp(points[points.length - 1]?.date);
      if (endTimestamp <= 0) {
        return points;
      }

      const startTimestamp = endTimestamp - (selected.days * DAY_MS);

      return points.filter((point) => {
        const pointTimestamp = toTimestamp(point?.date);
        return pointTimestamp >= startTimestamp;
      });
    },
    chartSeries() {
      const data = this.filteredBalanceEvolution.map((point) => Number(point?.balance || 0));
      return [
        {
          name: this.text.summary.general.fields.balance,
          data,
        },
      ];
    },
    chartRenderKey() {
      return `${this.selectedRange}-${this.filteredBalanceEvolution.length}-${this.currencyCode}`;
    },
    chartOptions() {
      const categories = this.filteredBalanceEvolution.map((point) => formatDateShort(point?.date, { fallback: '' }));

      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.32,
            opacityTo: 0.04,
            stops: [0, 90, 100],
          },
        },
        colors: ['#0b63b4'],
        xaxis: {
          categories,
          labels: {
            rotate: 0,
          },
        },
        yaxis: {
          forceNiceScale: true,
          decimalsInFloat: 0,
          labels: {
            minWidth: 82,
            formatter: (value) => formatAxisCurrency(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => formatAxisCurrency(value),
          },
        },
        grid: {
          borderColor: 'rgba(148, 163, 184, 0.25)',
          strokeDashArray: 3,
        },
      };
    },
    recentMovements() {
      const rows = Array.isArray(this.summaryData?.recent_movements)
        ? this.summaryData.recent_movements
        : [];

      return rows.slice(0, 5).map((row) => ({
        id: Number(row?.id || 0),
        date: String(row?.date || '').trim(),
        time: String(row?.time || '').trim(),
        account: String(row?.account || '').trim(),
        concept: String(row?.concept || '').trim(),
        category: String(row?.category || '').trim(),
        categoryKey: String(row?.categoryKey || row?.category_key || '').trim().toLowerCase(),
        typeReference: String(row?.typeReference || row?.type_reference || '').trim().toLowerCase(),
        accountMeta: String(row?.accountMeta || row?.account_meta || '').trim(),
        isIncome: Number(row?.isIncome ?? row?.is_income ?? 0) === 1,
        amountLabel: String(row?.amountLabel || row?.signed_amount_label || row?.amount_label || '').trim(),
      }));
    },
    recentTableHeaders() {
      const movementTable = this.text?.movements?.table || {};

      return {
        amount: String(movementTable.amount || 'Monto').trim() || 'Monto',
      };
    },
    generalItems() {
      return [
        this.item(this.text.summary.general.fields.name, this.account?.name),
        this.item(this.text.summary.general.fields.type, this.account?.type?.name),
        this.item(this.text.summary.general.fields.currency, this.accountCurrency),
        this.item(this.text.summary.general.fields.balance, this.formatMoney(this.account?.balance)),
        this.item(this.text.summary.general.fields.default, this.resolveYesNo(this.account?.is_default)),
        this.item(this.text.summary.general.fields.deposits || 'Mostrar datos de depósito en Facturas', this.resolveYesNo(this.account?.is_account_payment)),
        this.item(this.text.summary.general.fields.startDate, this.formatDate(this.account?.start_date)),
        this.item(this.text.summary.general.fields.startBalance, this.formatMoney(this.account?.start_balance)),
        this.item(this.text.summary.general.fields.creditLimit, this.formatMoney(this.account?.credit_limit)),
        this.item(this.text.summary.general.fields.breakDay, this.account?.break_day),
        this.item(this.text.summary.general.fields.paymentDay, this.account?.payment_day),
        this.item(this.text.summary.general.fields.status, this.statusLabel || this.resolveStatusLabel(this.account?.is_active)),
      ];
    },
    bankItems() {
      return [
        this.item(this.text.summary.bank.fields.bank, this.account?.bank?.name || this.account?.bank),
        this.item(this.text.summary.bank.fields.bankRfc, this.account?.bank?.rfc || this.account?.bank_rfc),
        this.item(this.text.summary.bank.fields.branch, this.account?.bank?.branch || this.account?.bank_branch),
        this.item(this.text.summary.bank.fields.holder, this.account?.holder),
        this.item(this.text.summary.bank.fields.accountNumber, this.account?.account_number),
        this.item(this.text.summary.bank.fields.cardNumber, this.account?.card_number),
        this.item(this.text.summary.bank.fields.clabe, this.account?.clabe),
        this.item(this.text.summary.bank.fields.swift, this.account?.swift),
        this.item(this.text.summary.bank.fields.aba, this.account?.aba),
      ];
    },
  },
  watch: {
    resolvedFinancialAccountId: {
      immediate: true,
      handler() {
        this.loadSummary();
      },
    },
    account: {
      deep: true,
      handler() {
        this.loadSummary();
      },
    },
  },
  methods: {
    async loadSummary() {
      if (this.resolvedFinancialAccountId <= 0) {
        return;
      }

      this.loadingSummary = true;

      try {
        const response = await financialAccountsService.detailSummary(this.resolvedFinancialAccountId, {}, { skipLoading: true });
        const payload = extractPayload(response);

        this.summaryData = {
          kpis: payload?.kpis || this.summaryData.kpis,
          recent_movements: Array.isArray(payload?.recent_movements) ? payload.recent_movements : [],
          balance_evolution: Array.isArray(payload?.balance_evolution) ? payload.balance_evolution : [],
        };
      } catch (error) {
        this.summaryData = {
          ...this.summaryData,
          recent_movements: [],
          balance_evolution: [],
        };
      } finally {
        this.loadingSummary = false;
      }
    },
    item(label, value) {
      const normalized = String(value ?? '').trim();
      return {
        label,
        value: normalized !== '' ? normalized : '',
      };
    },
    formatMoney(value) {
      if (value === null || value === undefined || String(value).trim() === '') {
        return this.fallbackValue;
      }

      return formatCurrencyDisplay(value, { currency: this.accountCurrency });
    },
    formatDate(value) {
      return formatDateLong(value, { fallback: this.fallbackValue });
    },
    resolveYesNo(value) {
      return normalizeYesNoLabel(value, {
        yesLabel: 'Si',
        noLabel: 'No',
        emptyLabel: '',
      });
    },
    resolveStatusLabel(isActive) {
      return Number(isActive || 0) === 1
        ? this.text.status.active
        : this.text.status.inactive;
    },
    resolveCategoryClass(categoryKey) {
      const normalized = String(categoryKey || '').trim().toLowerCase();
      if (normalized === 'income') return 'financial-category-pill--income';
      if (normalized === 'expense') return 'financial-category-pill--expense';
      if (normalized === 'transfer') return 'financial-category-pill--transfer';
      if (normalized === 'init') return 'financial-category-pill--init';
      if (normalized === 'otherincome') return 'financial-category-pill--otherincome';
      if (normalized === 'otherexpense') return 'financial-category-pill--otherexpense';
      return 'financial-category-pill--neutral';
    },
  },
};
</script>

<style scoped>
.app-data-table thead th.financial-col-center {
  text-align: center !important;
}

.financial-account-summary-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-summary-dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
  gap: 12px;
}

.financial-summary-kpi-card__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0 10px;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.5);
}

.financial-summary-kpi-card__header span {
  color: #64748b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.financial-summary-kpi-card__header strong {
  font-size: 28px;
  color: #0f172a;
}

.financial-summary-kpi-card__meta-grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.financial-summary-kpi-card__meta-grid > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(241, 245, 249, 0.8);
}

.financial-summary-kpi-card__meta-grid > div span {
  color: #64748b;
  font-size: 12px;
}

.financial-summary-kpi-card__meta-grid > div strong {
  color: #0f172a;
  font-size: 17px;
}

.financial-summary-kpi-card {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.financial-summary-chart-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.financial-summary-chart-card__header h4 {
  margin: 0;
}

.financial-summary-chart-card__ranges {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.financial-summary-chart-card__range-btn {
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 999px;
  min-height: 28px;
  padding: 0 10px;
  background: #ffffff;
  color: #334155;
  font-size: 11px;
  font-weight: 700;
}

.financial-summary-chart-card__range-btn.active {
  background: rgba(0, 112, 205, 0.12);
  color: #0b63b4;
  border-color: rgba(0, 112, 205, 0.42);
}

.financial-summary-chart-card__body {
  margin-top: 8px;
}

.financial-summary-recent-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.financial-summary-recent-card__header h4 {
  margin: 0;
}

.financial-summary-recent-card__link {
  border: 1px solid transparent;
  background: transparent;
  color: #0070cd;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.financial-summary-recent-card__link:hover {
  background: rgba(0, 112, 205, 0.08);
  border-color: rgba(0, 112, 205, 0.22);
  color: #005eae;
}

.financial-summary-recent-card__body {
  margin-top: 8px;
}

.financial-summary-recent-card__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.financial-summary-recent-card__item {
  display: grid;
  grid-template-columns: 130px 1fr auto auto;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.35);
}

.financial-summary-recent-card__item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.financial-summary-recent-card__date-wrap,
.financial-summary-recent-card__concept-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.financial-summary-recent-card__date {
  color: #334155;
  font-weight: 700;
}

.financial-summary-recent-card__concept {
  color: #0f172a;
}

.financial-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
}

.financial-category-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 999px;
  padding: 0 10px;
  font-size: 11px;
  border: 1px solid transparent;
}

.financial-category-pill--income {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.financial-category-pill--expense {
  color: #9f1239;
  background: #ffe4e6;
  border-color: #fda4af;
}

.financial-category-pill--transfer {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}

.financial-category-pill--init {
  color: #6b21a8;
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.financial-category-pill--otherincome {
  color: #065f46;
  background: #ccfbf1;
  border-color: #99f6e4;
}

.financial-category-pill--otherexpense {
  color: #7c2d12;
  background: #ffedd5;
  border-color: #fdba74;
}

.financial-category-pill--neutral {
  color: #334155;
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.financial-amount-positive {
  color: #16a34a;
}

.financial-amount-negative {
  color: #dc2626;
}

@media (max-width: 900px) {
  .financial-summary-dashboard-grid {
    grid-template-columns: 1fr;
  }

  .financial-summary-recent-card__item {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}
</style>
